import { AABB, ObjectId, View } from "@novorender/api";
import { BoundingSphere, HierarcicalObjectReference, ObjectData } from "@novorender/data-js-api";
import { quat, ReadonlyVec3, vec3 } from "gl-matrix";
import pMap from "p-map";

// Type-only: `dataAccess`' barrel reaches back into this module through
// `objectDbDataAccess`, so a value import here is a genuine cycle.
import type { ObjectDataAccess } from "dataAccess";
import { flip } from "features/render/utils";
import { NodeType } from "types/misc";

import { finiteSphereToAabb, isFiniteAabb3, mergeBoundingSpheres, unionAABB3 } from "./math";
import { getFilePathFromObjectPath, getTopmostFilePathFromObjectPath, isLeafNode } from "./objectPath";
import { IdList } from "./selectionSet";
export {
    getAncestorSearchDepth,
    getFilePathFromObjectPath,
    getNodeClass,
    getParentPaths,
    getTopmostFilePathFromObjectPath,
    isContainerFileNode,
    isFileNode,
    isLeafNode,
    NODE_CLASS_PROPERTY,
    NodeClass,
} from "./objectPath";

/** Property groups generated by the IFC parser that we hide for now (BM-11063). */
const IFC_HIDDEN_GROUPS = new Set(["Quantities", "Location"]);

export function decodeObjPathName(str: string) {
    try {
        return decodeURIComponent(str);
    } catch {
        return str.replace(/%2f/gi, "/").replace(/%20/gi, " ");
    }
}

export function getParentPath(path: string): string {
    const lastSlashIdx = path.lastIndexOf("/");
    return lastSlashIdx > 0 ? path.slice(0, lastSlashIdx) : "";
}

export function extractObjectIds<T extends { id: number } = HierarcicalObjectReference>(
    objects: { id: number }[],
): T["id"][] {
    return objects.map((obj) => obj.id);
}

export function getObjectNameFromPath(path: string): string {
    const arr = path.split("/");

    return decodeObjPathName(arr.at(-1) ?? path);
}

export async function getObjectMetadataRotation(
    dataAccess: ObjectDataAccess,
    objectId: ObjectId,
    signal?: AbortSignal,
): Promise<quat | undefined> {
    const metadata = await dataAccess.getObject(objectId, signal);
    if (!metadata) {
        return;
    }

    const filePath = getFilePathFromObjectPath(metadata.path);
    if (!filePath) {
        return;
    }

    const [descendantName] = metadata.path.substring(filePath.length + 1).split("/", 1);
    if (!descendantName) {
        return;
    }

    const node = await dataAccess.getObjectAtPath(`${filePath}/${descendantName}`, signal);
    const rotationProp = node?.properties.find((p) => p[0] === "Novorender/Rotation")?.[1];

    return rotationProp ? JSON.parse(rotationProp) : undefined;
}

/**
 * The object's own bounds, reading it only when the row does not already carry them.
 *
 * Every backend puts bounds on the rows it hands back, an object DB's search rows and a
 * BIM index's alike, so the read is a belt for a row that arrives without them rather
 * than the normal path — not a request per ancestor.
 */
async function boundsOf(
    dataAccess: ObjectDataAccess,
    object: HierarcicalObjectReference,
    signal?: AbortSignal,
): Promise<ObjectData["bounds"] | undefined> {
    return object.bounds ?? (await dataAccess.getObject(object.id, signal))?.bounds;
}

/**
 * Determines whether an object node represents a PDF/drawing document.
 *
 * PDFs normally carry a ".pdf" extension, but some (notably ones created via model
 * federation) are stored without a (proper) extension. Those still need to be
 * filtered out of the model tree, so when the path doesn't resolve to a recognized
 * model-file extension we fall back to the "Procore/DocumentSource" metadata property
 * (which requires loading the object metadata) and treat a **leaf** stamped
 * `drawings` or `DOCUMENTS` as a 2D document (Drawings-tool sheets and Documents-tool
 * files). A `DOCUMENTS` node with descendants is a federated model root, not a sheet.
 *
 * To avoid unnecessary metadata loads, any node whose path already resolves to a
 * recognized, non-PDF model-file extension is treated as a non-PDF without
 * inspecting its metadata.
 *
 * @param options.knownPdfObjectIds The scene's document ids from Procore's discovery
 * (`utils/pdfObjectIds`), which admits a search hit only when its path satisfies the same
 * rule as below. Given one, it answers on its own and neither path nor metadata is read: a
 * hit is a document, a miss is not. Pass it only once discovery has finished — the callers'
 * `readyPdfObjectIds` is `undefined` until then — since a partial set would pass a sheet off
 * as a model.
 * @see isPdfOrDrawingObjectId in `dataAccess/utils` for the variant that holds only an id.
 */
export async function isPdfOrDrawingObject(
    object: HierarcicalObjectReference,
    options?: { knownPdfObjectIds?: Pick<Set<ObjectId>, "has"> },
): Promise<boolean> {
    if (options?.knownPdfObjectIds) {
        return options.knownPdfObjectIds.has(object.id);
    }

    const filePath = getFilePathFromObjectPath(object.path);
    if (filePath) {
        return filePath.toLowerCase().endsWith(".pdf");
    }

    if (!isLeafNode(object)) {
        return false;
    }

    try {
        const metadata = await object.loadMetaData();
        return hasDrawingsOrDocumentsSource(metadata.properties);
    } catch {
        return false;
    }
}

/**
 * Server-generated Procore document source: Drawings-tool sheets (`drawings`) and
 * Documents-tool files (`DOCUMENTS`). Callers also require a leaf node: both are 2D
 * files that should stay out of the object tree and the reset flyout's hidden count,
 * while a `DOCUMENTS` model root with descendants must not.
 */
export function hasDrawingsOrDocumentsSource(properties: readonly (readonly [key: string, value: string])[]): boolean {
    const documentSource = properties.find(([key]) => key === "Procore/DocumentSource")?.[1]?.toLowerCase();
    return documentSource === "drawings" || documentSource === "documents";
}

function getBoxBoundsW(box: AABB, normal: ReadonlyVec3): { min: number; max: number } | undefined {
    const corners = [
        vec3.fromValues(box.min[0], box.min[1], box.min[2]),
        vec3.fromValues(box.min[0], box.min[1], box.max[2]),
        vec3.fromValues(box.min[0], box.max[1], box.min[2]),
        vec3.fromValues(box.min[0], box.max[1], box.max[2]),
        vec3.fromValues(box.max[0], box.min[1], box.min[2]),
        vec3.fromValues(box.max[0], box.min[1], box.max[2]),
        vec3.fromValues(box.max[0], box.max[1], box.min[2]),
        vec3.fromValues(box.max[0], box.max[1], box.max[2]),
    ];

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const corner of corners) {
        const w = vec3.dot(corner, normal);
        min = Math.min(min, w);
        max = Math.max(max, w);
    }

    return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : undefined;
}

export async function getObjectBoundsW(
    dataAccess: ObjectDataAccess,
    objectId: ObjectId,
    normal: ReadonlyVec3,
    signal?: AbortSignal,
): Promise<{ min: number; max: number } | undefined> {
    const box = (await dataAccess.getBounds({ internalIds: [objectId] }, { parts: "box", signal }))?.box;
    if (!box) {
        return;
    }

    return getBoxBoundsW(box, normal);
}

/** Below this the object's own extent along the normal is too thin to place a plane by. */
const MIN_RANGE_SIZE = 5;

export async function getBigEnoughObjectBoundsW(
    dataAccess: ObjectDataAccess,
    objectId: ObjectId,
    normal: ReadonlyVec3,
    signal?: AbortSignal,
): Promise<{ min: number; max: number } | undefined> {
    const object = await dataAccess.getObject(objectId, signal);
    if (!object?.bounds?.box) {
        return;
    }

    const objectRange = getBoxBoundsW(object.bounds.box, normal);
    if (objectRange && objectRange.max - objectRange.min >= MIN_RANGE_SIZE) {
        return objectRange;
    }

    // Traverse objects up the hierarchy until we find a range that is big enough.
    try {
        const ancestors = await dataAccess.getAncestors(object, signal);

        // Deepest first: the nearest ancestor that is big enough frames the plane
        // tightest. `getAncestors` answers root first.
        for (const ancestor of ancestors.toReversed()) {
            const box = (await boundsOf(dataAccess, ancestor, signal))?.box;
            if (!box) {
                continue;
            }
            const range = getBoxBoundsW(box, normal);
            if (range && range.max - range.min >= MIN_RANGE_SIZE) {
                return range;
            }
        }
    } catch (error) {
        console.error(error);
    }
}

export function getFileNameFromPath(path: string): string | null {
    const filePath = getFilePathFromObjectPath(path);

    if (!filePath) {
        return null;
    }

    const fileName = filePath.includes("/") ? (filePath.split("/").at(-1) ?? null) : filePath;
    return fileName ? decodeObjPathName(fileName) : null;
}

/**
 * Like {@link getFileNameFromPath}, but based on the outermost/topmost file node in
 * the path (see {@link getTopmostFilePathFromObjectPath}) rather than the innermost
 * one. Strips any preceding path fragments (e.g. a federation root node's name), so
 * this always returns a bare file name rather than a partial path.
 */
export function getTopmostFileNameFromPath(path: string): string | null {
    const filePath = getTopmostFilePathFromObjectPath(path);

    if (!filePath) {
        return null;
    }

    const fileName = filePath.includes("/") ? (filePath.split("/").at(-1) ?? null) : filePath;
    return fileName ? decodeObjPathName(fileName) : null;
}

/**
 * The axis-aligned box enclosing a selection, in the scene's own space — unflipped on
 * purpose. {@link getSelectionBoundingSphere} optionally remaps CAD Y-up into world
 * space; this helper does not, matching how SpaceMouse historically framed Fit.
 *
 * Prefer this over {@link getSelectionBoundingSphere} when the consumer wants the
 * selection's real dimensions. Boxing a bounding sphere widens every axis to the
 * object's full diagonal, which is dramatic for the flat and elongated shapes BIM is
 * made of: a 10 x 0.2 x 3 m wall boxes out to a 10.4 m cube. Members that only have a
 * sphere still frame: the box is asked for first (one probe on the common BIM path);
 * a sphere is fetched and boxed only when that box is missing or non-finite.
 */
export async function getSelectionBoundingBox({
    ids,
    dataAccess,
    abortSignal,
}: {
    ids: IdList;
    dataAccess: ObjectDataAccess;
    abortSignal?: AbortSignal;
}): Promise<AABB | undefined> {
    const boxed = await dataAccess.getBounds({ internalIds: ids }, { parts: "box", signal: abortSignal });
    if (boxed?.box && isFiniteAabb3(boxed.box)) {
        return boxed.box as AABB;
    }
    const spherical = await dataAccess.getBounds({ internalIds: ids }, { parts: "sphere", signal: abortSignal });
    return finiteSphereToAabb(spherical?.sphere);
}

/**
 * The sphere enclosing a selection, in world space.
 *
 * @param params.flipCoords Flip CAD (Y-up) centers into world space. Pass
 * `!vec3.equals(scene.up ?? [0, 1, 0], [0, 0, 1])` — bounds come back in the scene's own
 * space, which is Z-up only for scenes that say so.
 */
export async function getSelectionBoundingSphere({
    ids,
    dataAccess,
    abortSignal,
    flipCoords,
}: {
    ids: IdList;
    dataAccess: ObjectDataAccess;
    abortSignal?: AbortSignal;
    flipCoords?: boolean;
}): Promise<BoundingSphere | undefined> {
    const sphere = (await dataAccess.getBounds({ internalIds: ids }, { parts: "sphere", signal: abortSignal }))?.sphere;

    return sphere && flipCoords ? { ...sphere, center: flip(sphere.center) } : sphere;
}

export function getTotalBoundingSphere(
    nodes: HierarcicalObjectReference[],
    options?: { flip?: boolean },
): BoundingSphere | undefined {
    const spheres: BoundingSphere[] = [];

    const shouldFlip = options?.flip === true;
    for (const node of nodes) {
        const sphere = node.bounds?.sphere;

        if (sphere) {
            spheres.push(
                shouldFlip
                    ? {
                          ...sphere,
                          center: flip(sphere.center),
                      }
                    : sphere,
            );
        }
    }

    return mergeBoundingSpheres(spheres);
}

/**
 * Resolves a combined bounding sphere for a set of top-level selections.
 *
 * Returns undefined when none of the entries resolve to a sphere, letting the
 * caller fall back to batchedPropertySearch.
 */
export async function resolveTopLevelBoundingSphere({
    topLevelSelections,
    dataAccess,
    abortSignal,
    flipCoords,
}: {
    topLevelSelections: Record<number, { path: string }>;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    flipCoords?: boolean;
}): Promise<BoundingSphere | undefined> {
    const nodes = await collectTopLevelBoundsNodes({
        topLevelSelections,
        dataAccess,
        abortSignal,
        carriesBounds: (node) => Boolean(node.bounds?.sphere),
    });
    return nodes && getTotalBoundingSphere(nodes, { flip: flipCoords });
}

/**
 * Walks fully-selected tree nodes down to the first descendants that carry their own bounds.
 *
 * Folder nodes often already have bounds (O(1) cache hit). Otherwise walk children up to
 * MAX_FALLBACK_DEPTH. Empty or aborted walks return `undefined`, not a partial fold.
 */
async function collectTopLevelBoundsNodes({
    topLevelSelections,
    dataAccess,
    abortSignal,
    carriesBounds,
}: {
    topLevelSelections: Record<number, { path: string }>;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    carriesBounds: (node: HierarcicalObjectReference) => boolean;
}): Promise<HierarcicalObjectReference[] | undefined> {
    const ids = Object.keys(topLevelSelections);
    if (ids.length === 0) {
        return undefined;
    }

    const MAX_FALLBACK_DEPTH = 3;
    const collected: HierarcicalObjectReference[] = [];

    const searchQueue: HierarcicalObjectReference[] = [];

    await pMap(
        ids,
        async (idStr) => {
            if (abortSignal.aborted) {
                return;
            }
            const id = Number(idStr);
            const data = await dataAccess.getObject(id, abortSignal);
            if (data && carriesBounds(data)) {
                collected.push(data);
                return;
            }
            // No bounds on the node itself — queue a shallow search.
            if (data && data.type !== NodeType.Leaf) {
                searchQueue.push(data);
            }
        },
        { concurrency: 10 },
    );

    if (abortSignal.aborted) {
        return undefined;
    }

    // For nodes without direct bounds, walk one level at a time up to
    // MAX_FALLBACK_DEPTH until we find children that have them.
    let currentNodes = searchQueue;
    for (let depth = 0; depth < MAX_FALLBACK_DEPTH && currentNodes.length > 0; depth++) {
        const nextNodes: HierarcicalObjectReference[] = [];
        await pMap(
            currentNodes,
            async (parent) => {
                if (abortSignal.aborted) {
                    return;
                }
                const iter = dataAccess.getChildren(parent, abortSignal);
                for await (const child of iter) {
                    if (abortSignal.aborted) {
                        return;
                    }
                    if (carriesBounds(child)) {
                        collected.push(child);
                    } else if (child.type !== NodeType.Leaf) {
                        nextNodes.push(child);
                    }
                }
            },
            { concurrency: 10 },
        );

        if (abortSignal.aborted) {
            return undefined;
        }

        currentNodes = nextNodes;
    }

    return collected.length > 0 ? collected : undefined;
}

/**
 * Unflipped AABB of fully-selected tree nodes. Prefers each node's box; boxes its
 * sphere only when the box is missing, so elongated geometry still frames tightly.
 */
export async function resolveTopLevelBoundingBox({
    topLevelSelections,
    dataAccess,
    abortSignal,
}: {
    topLevelSelections: Record<number, { path: string }>;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
}): Promise<AABB | undefined> {
    const nodes = await collectTopLevelBoundsNodes({
        topLevelSelections,
        dataAccess,
        abortSignal,
        carriesBounds: (node) =>
            Boolean((node.bounds?.box && isFiniteAabb3(node.bounds.box)) || finiteSphereToAabb(node.bounds?.sphere)),
    });
    if (!nodes) {
        return undefined;
    }

    const boxes: AABB[] = [];
    for (const node of nodes) {
        const box = node.bounds?.box;
        if (box && isFiniteAabb3(box)) {
            boxes.push(box);
            continue;
        }
        const boxed = finiteSphereToAabb(node.bounds?.sphere);
        if (boxed) {
            boxes.push(boxed);
        }
    }
    return unionAABB3(boxes);
}

export function getGuids(refs: HierarcicalObjectReference[]): Promise<string[]> {
    return Promise.all(refs.map((ref) => getGuid(ref))).then((guids) => guids.filter((guid) => guid !== ""));
}

export function getGuid(ref: HierarcicalObjectReference): Promise<string> {
    return ref.loadMetaData().then((obj) => {
        const guid = obj.properties.find((prop) => prop[0] === "GUID");
        return guid ? guid[1] : "";
    });
}

export function getPropertyDisplayName(property: string): string {
    const display = decodeObjPathName(property);

    return display[0]?.toUpperCase() + display.slice(1);
}

export function isUrl(str: string): boolean {
    return str.startsWith("http");
}

/**
 * Decode URL-encoded property values for display. Leaves URLs unchanged.
 * Any valid `%XX` sequence is rewritten (`"Grade %41"` → `"Grade A"`); malformed
 * sequences throw in `decodeURIComponent` and fall through to a slash/space-only
 * replacement, so they stay as-is.
 */
export function getPropertyDisplayValue(value: string): string {
    if (!value || isUrl(value)) {
        return value;
    }

    return decodeObjPathName(value);
}

export function isTerrainObjectId(objectId: ObjectId) {
    return objectId <= View.terrainMaxId;
}

/**
 * Should the property be hidden in Properties, Property Tree, and other places?
 * NOTE: Not all internal properties are hidden.
 */
export function shouldHideProperty(property: string): boolean {
    const lc = property.toLowerCase();
    return lc === "procore" || lc.startsWith("procore/");
}

/** Whether the object belongs to an IFC file, based on its path. */
export function isIfcObjectPath(path: string): boolean {
    const filePath = getFilePathFromObjectPath(path);
    return filePath != null && /\.ifc$/i.test(filePath);
}

/**
 * Adjusts a property key for IFC objects (BM-11063):
 * - returns `null` for properties in hidden groups (see {@link IFC_HIDDEN_GROUPS})
 *
 * Properties without a group and properties in other groups are returned unchanged.
 */
export function transformIfcPropertyKey(property: string): string | null {
    const slashIdx = property.indexOf("/");
    if (slashIdx === -1) {
        return property;
    }

    const group = decodeObjPathName(property.slice(0, slashIdx));

    if (IFC_HIDDEN_GROUPS.has(group)) {
        return null;
    }

    return property;
}

/**
 * Filters an object's raw properties for display: drops empty and internal
 * (Procore/*) properties, and—only in the Procore Design (`isProcoreDesign`)—applies
 * IFC-specific group adjustments for objects in IFC files
 * (see {@link transformIfcPropertyKey}).
 */
export function getDisplayProperties(
    object: Pick<ObjectData, "path" | "properties">,
    isProcoreDesign = false,
): [string, string][] {
    const isIfc = isProcoreDesign && isIfcObjectPath(object.path);
    const result: [string, string][] = [];

    for (const [property, value] of object.properties) {
        if (!value || shouldHideProperty(property)) {
            continue;
        }

        const key = isIfc ? transformIfcPropertyKey(property) : property;
        if (key !== null) {
            result.push([key, value]);
        }
    }

    return result;
}
